import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SearchIcon from '@mui/icons-material/Search';
import { Chip, IconButton, Input, Paper, Tooltip } from '@mui/material';
import {
  AdvancedSearchComponentType,
  AdvancedSearchWrapper,
  pluralize,
  SearchParams,
} from '@prismamedia/one-components';
import classNames from 'classnames';
import { debounce } from 'lodash';
import React, { ChangeEvent, FC, useCallback, useRef, useState } from 'react';
import { MEDIA_TYPES_LABEL, MultiMediaFetchItemsType } from '../types';
import { useStyles } from './styles';

interface MultimediaSearchProps {
  className?: string;
  displayMode?: JSX.Element;
  placeholder?: string;
  pluginType?: MEDIA_TYPES_LABEL;
  advancedSearch?: AdvancedSearchComponentType;
  fetchItems?: MultiMediaFetchItemsType;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  searchParams: SearchParams;
}

export const MultimediaSearch: FC<MultimediaSearchProps> = ({
  className,
  displayMode,
  placeholder,
  advancedSearch,
  pluginType,
  setSearchParams,
  searchParams,
  fetchItems,
}) => {
  const classes = useStyles();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedDisplayed = advancedOpen && !!advancedSearch;
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState(searchParams.search);
  const debouncedOnChangeSearch = debounce(setSearchParams, 500);

  const handleInputChange = useCallback(
    (newValue: string) => {
      setSearchValue(newValue);
      debouncedOnChangeSearch((prevState) => ({
        ...prevState,
        search: newValue,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const allowedMediasTypesForAdvancedSearch = [
    MEDIA_TYPES_LABEL.ARTICLES,
    MEDIA_TYPES_LABEL.VIDEOS,
    MEDIA_TYPES_LABEL.SLIDESHOWS,
  ];

  const searchParamsCount = Object.keys(searchParams).filter(
    (key) => key !== 'search',
  ).length;

  const searchWithParams = () => {
    fetchItems && fetchItems();
    setAdvancedOpen(false);
  };

  const resetSearch = ({ resetAll }: { resetAll: boolean }) => {
    setSearchParams(resetAll ? {} : { search: searchParams.search });
  };

  const displayAdvancedSearchIcon =
    pluginType &&
    allowedMediasTypesForAdvancedSearch.includes(pluginType) &&
    advancedSearch &&
    !searchParamsCount;

  const displaySearchIcon =
    pluginType &&
    (!allowedMediasTypesForAdvancedSearch.includes(pluginType) ||
      (!advancedSearch && fetchItems));

  return (
    <div className={classNames(classes.wrapper, className)}>
      <Paper
        variant="outlined"
        className={classes.paperSearch}
        ref={wrapperRef}
      >
        <div className={classes.searchContainer}>
          <Input
            autoFocus
            data-testid="search-input"
            value={searchValue}
            fullWidth
            className={classes.input}
            disableUnderline
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              event.preventDefault();
              handleInputChange(event.target.value);
            }}
            placeholder={placeholder || 'Rechercher un contenu'}
          />
        </div>
        {!!searchParamsCount && (
          <>
            <Tooltip
              title={
                advancedOpen
                  ? 'Fermer la fenêtre de recherche'
                  : 'Voir les critères de recherche'
              }
            >
              <Chip
                variant="outlined"
                label={`+ ${searchParamsCount} ${pluralize(
                  'critère',
                  searchParamsCount,
                )}`}
                onClick={() => {
                  setAdvancedOpen((open) => !open);
                }}
              />
            </Tooltip>
            <Tooltip title="Effacer les critères de recherche">
              <IconButton
                className={classes.icon}
                onClick={() => {
                  resetSearch({ resetAll: false });
                  searchWithParams();
                }}
              >
                <DeleteSweepOutlinedIcon />
              </IconButton>
            </Tooltip>
          </>
        )}

        {advancedSearch && (
          <AdvancedSearchWrapper
            advancedSearch={advancedSearch}
            anchorRef={wrapperRef}
            onClose={searchWithParams}
            open={advancedDisplayed}
            resetSearch={() => {
              resetSearch({ resetAll: false });
            }}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            triggerSearch={() => {
              searchWithParams();
              inputRef.current?.focus();
            }}
          />
        )}
        {displayAdvancedSearchIcon && (
          <Tooltip title="Recherche avancée">
            <IconButton
              onClick={() => {
                setAdvancedOpen((prev) => !prev);
              }}
            >
              <ManageSearchIcon className={classes.manageSearchIcon} />
            </IconButton>
          </Tooltip>
        )}
        {displaySearchIcon && (
          <IconButton
            onClick={() => {
              fetchItems && fetchItems();
            }}
            className={classNames(classes.iconButton, {
              [classes.iconButtonHidden]: searchParams?.search?.length,
            })}
          >
            <SearchIcon />
          </IconButton>
        )}

        {!!searchParams?.search?.length && (
          <Tooltip title="Effacer le texte de la recherche">
            <IconButton
              onClick={() => {
                setSearchValue('');
                setSearchParams((searchParamsPrevState) => {
                  return { ...searchParamsPrevState, search: '' };
                });
              }}
              className={classNames(classes.iconButton)}
            >
              <BackspaceOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        {displayMode}
      </Paper>
    </div>
  );
};
